import React from 'react';
import moment from 'moment-timezone';
import { observer } from 'mobx-react';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import {
	candidateHiringOptions,
	candidateSeniorityOptions
} from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import { applicantChangelogStore } from './ApplicantChangelogStore';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';

const TABLE_COLUMNS = {
	TYPE: 'columnName',
	DESCRIPTION: 'description',
	DATE: 'createdAt',
	USER: 'user.name'
};

const getDisplayDescription = row => {
	const { columnName, description, newValue } = row;

	const descriptionMapping = {
		englishLevel: () => {
			return candidatesStore.englishLevelOptions.find(
				option => option.value === newValue
			)?.text;
		},
		seniority: () => {
			return candidateSeniorityOptions.find(option => option.value === newValue)
				?.text;
		},
		hireability: () => {
			return (
				candidateHiringOptions.find(option => option.value === newValue)
					?.text || 'Empty'
			);
		}
	};

	const mappingFunction = descriptionMapping[columnName];
	return mappingFunction ? mappingFunction() : description || '';
};

const getColumnDisplayName = columnName => {
	return applicantChangelogStore.typeOptions.find(
		option => option.value === columnName
	)?.text;
};

function ApplicantChangelogTable({
	data,
	sort,
	onSort,
	isEmpty,
	emptyState,
	loading
}) {
	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;

		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th
					style={{ width: '13%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.TYPE)}>
					Type {isColumnSorted(TABLE_COLUMNS.TYPE)}
				</th>
				<th
					style={{ width: '20%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.DESCRIPTION)}>
					Details {isColumnSorted(TABLE_COLUMNS.DESCRIPTION)}
				</th>
				<th
					style={{ width: '30%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.DATE)}>
					Date {isColumnSorted(TABLE_COLUMNS.DATE)}
				</th>
				<th style={{ width: '20%' }}>User</th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		return (
			<tr key={row.id}>
				<td className="is-capitalized">
					{getColumnDisplayName(row.columnName) || row.columnName}
				</td>
				<td style={{ overflowWrap: 'anywhere' }}>
					{getDisplayDescription(row)}
				</td>
				<td>{moment(row.createdAt).format('lll')}</td>
				<td>
					{row.user && (
						<UserProfileAvatar
							size={32}
							imageUrl={row.user?.imageUrl}
							name={row.user?.name}
							tooltipPosition="top"
						/>
					)}
				</td>
			</tr>
		);
	};

	return (
		<>
			<table className="table is-fullwidth">
				{renderTableHeader()}
				{loading && data.length === 0 ? (
					<AtsTableSkeleton columns={4} rows={6} />
				) : (
					!isEmpty && <tbody>{data.map(renderTableRow)}</tbody>
				)}
			</table>
			{isEmpty ? emptyState : null}
		</>
	);
}

export default observer(ApplicantChangelogTable);
